import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { CmsService } from '@spartacus/core';
import { CmsComponentData } from '@spartacus/storefront';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { CmsLinkCollectionComponent, CmsLinkCollectionItemComponent } from '../../../core/model';

export interface AlphabeticalLink {
  url: string;
  label: string;
  target: string | null;
}

export interface AlphabeticalLinksContainer {
  label: string;
  links: AlphabeticalLink[];
}

@Component({
  selector: 'py-alphabetical-link-container',
  templateUrl: './alphabetical-link-container.component.html',
  styleUrls: ['./alphabetical-link-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AlphabeticalLinkContainerComponent implements OnInit {
  linkContainers$: Observable<AlphabeticalLinksContainer[]>;
  subscriptions: Subscription = new Subscription();

  constructor(
    public cmsComponent: CmsComponentData<CmsLinkCollectionComponent>,
    public cmsService: CmsService
  ) {}

  ngOnInit(): void {
    this.linkContainers$ = this.cmsComponent.data$.pipe(
      filter((data) => !!data),
      take(1),
      map((data) =>
        data.links?.split(' ').map((link) =>
          this.cmsService.getComponentData(link).pipe(
            filter((d) => !!d),
            take(1)
          )
        )
      ),
      switchMap((links$) => forkJoin(links$)),
      map((d) => this.buildAlpabeticalList(d))
    );
  }

  private buildAlpabeticalList(links: CmsLinkCollectionItemComponent[]): AlphabeticalLinksContainer[] {
    const alphabeticalItems: AlphabeticalLinksContainer[] = [];

    links.forEach((link) => {
      const linkLabel = link.linkName || link.name;

      if (linkLabel && linkLabel[0] && link.url) {
        const letterLabel = linkLabel[0].toUpperCase();

        if (alphabeticalItems.find((alphabeticalItem) => alphabeticalItem.label === letterLabel)) {
          const index = alphabeticalItems.map((alphabeticalItem) => alphabeticalItem.label).indexOf(letterLabel);

          alphabeticalItems[index].links.push({
            url: link.url,
            label: linkLabel,
            target: this.getTarget(link),
          });
        } else {
          alphabeticalItems.push({
            label: letterLabel,
            links: [
              {
                url: link.url,
                label: linkLabel,
                target: this.getTarget(link),
              },
            ],
          });
        }
      }
    });

    return alphabeticalItems;
  }

  private getTarget(link: CmsLinkCollectionItemComponent): string | null {
    return link.target === 'true' || link.target === true ? '_blank' : null;
  }
}
